import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, User, ArrowLeft } from 'lucide-react';

const SelfPublishingVsTraditional = () => {
  return (
    <>
      <Helmet>
        <title>Self-Publishing vs Traditional Publishing | Rushmore Publishing</title>
        <meta name="description" content="Compare self-publishing and traditional publishing to make the right choice for your book." />
      </Helmet>

      <article className="min-h-screen bg-white pt-24 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 mb-8 font-semibold">
            <ArrowLeft className="w-5 h-5" /> Back to Blog
          </Link>

          <div className="mb-8">
            <span className="bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold">
              Publishing
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Self-Publishing vs Traditional Publishing: Making the Right Choice
          </h1>

          <div className="flex items-center gap-6 text-gray-600 mb-8 pb-8 border-b border-gray-200">
            <span className="flex items-center gap-2"><User className="w-5 h-5" />Michael Chen</span>
            <span className="flex items-center gap-2"><Calendar className="w-5 h-5" />Jan 12, 2025</span>
            <span className="flex items-center gap-2"><Clock className="w-5 h-5" />7 min read</span>
          </div>

          <img src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Publishing Choice" className="w-full h-96 object-cover rounded-2xl mb-12" />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Choosing between self-publishing and traditional publishing is one of the most important decisions you'll make as an author. Let's break down both options.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Traditional Publishing</h2>
            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Pros:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>Professional Support:</strong> Access to experienced editors, designers, and marketers</li>
              <li><strong>Distribution:</strong> Wide bookstore placement and library access</li>
              <li><strong>Credibility:</strong> Validation from industry professionals</li>
              <li><strong>Advances:</strong> Potential upfront payment for your work</li>
              <li><strong>No Upfront Costs:</strong> Publisher covers all production expenses</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Cons:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>Lower Royalties:</strong> Typically 10-15% compared to 70% for self-publishing</li>
              <li><strong>Long Timeline:</strong> Can take 1-2 years from acceptance to publication</li>
              <li><strong>Loss of Control:</strong> Publisher makes final decisions on cover, title, and edits</li>
              <li><strong>Difficult to Break In:</strong> High rejection rates, especially for new authors</li>
              <li><strong>Rights Issues:</strong> You may lose some rights to your work</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Self-Publishing</h2>
            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Pros:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>Higher Royalties:</strong> Keep 35-70% of sales revenue</li>
              <li><strong>Full Control:</strong> You decide everything from cover to content</li>
              <li><strong>Faster Timeline:</strong> Publish in weeks or months, not years</li>
              <li><strong>Keep All Rights:</strong> You own your work completely</li>
              <li><strong>Direct Connection:</strong> Build relationships directly with your readers</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Cons:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>Upfront Investment:</strong> You pay for editing, design, and marketing</li>
              <li><strong>Time-Consuming:</strong> You manage every aspect of publishing</li>
              <li><strong>Marketing Burden:</strong> All promotion falls on you</li>
              <li><strong>Limited Distribution:</strong> Harder to get into physical bookstores</li>
              <li><strong>Quality Concerns:</strong> Must ensure professional standards without publisher oversight</li>
            </ul>

            <div className="bg-amber-50 border-l-4 border-amber-700 p-6 my-12 rounded-r-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Which Should You Choose?</h3>
              <p className="text-gray-700 mb-4"><strong>Choose Traditional Publishing if:</strong></p>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>✓ You want industry validation and credibility</li>
                <li>✓ You prefer focusing solely on writing</li>
                <li>✓ Wide physical distribution is important to you</li>
                <li>✓ You're willing to wait and accept lower royalties</li>
              </ul>
              <p className="text-gray-700 mb-4"><strong>Choose Self-Publishing if:</strong></p>
              <ul className="space-y-2 text-gray-700">
                <li>✓ You want maximum control and higher royalties</li>
                <li>✓ You can invest time and money upfront</li>
                <li>✓ You want to publish quickly</li>
                <li>✓ You're comfortable with marketing and business aspects</li>
              </ul>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6">
              There's no universally "right" answer - it depends on your goals, resources, and what matters most to you as an author. Many successful authors have used both paths at different points in their careers.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              At Rushmore Publishing, we offer hybrid services that give you the best of both worlds - professional support with author control. Whatever path you choose, we're here to help you succeed.
            </p>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-gray-600 mb-6">Ready to publish your book?</p>
            <Link to="/" className="inline-block">
              <button className="blob-btn">
                Get Started Today
                <span className="blob-btn__inner">
                  <span className="blob-btn__blobs">
                    <span className="blob-btn__blob"></span>
                    <span className="blob-btn__blob"></span>
                    <span className="blob-btn__blob"></span>
                    <span className="blob-btn__blob"></span>
                  </span>
                </span>
              </button>
            </Link>
          </div>
        </div>
      </article>
    </>
  );
};

export default SelfPublishingVsTraditional;